import { underlyingsDatesApiConnector } from 'apiConnector/underlyings-dates';
import { ofType } from 'redux-observable';
import { catchError, filter, map, mergeMap, Observable, of, SchedulerLike, timeout } from 'rxjs';
import { EpicWithThunk } from 'state/state.common';
import {
  underlyingsBlackoutDatesReceived,
  underlyingsBlackoutDatesRequestFailed,
  underlyingsSpecificDatesReceived,
  underlyingsSpecificDatesRequestFailed,
} from 'state/underlyingsDates/underlyingsDates.action';
import { buildUnderlyingsDatesKey } from 'state/underlyingsDates/underlyingsDates.utils';
import {
  UnderlyingsDatesBlackoutDatesResponse,
  UnderlyingsDatesPayload,
  UnderlyingsDatesSpecificDatesResponse,
} from 'state/underlyingsDates/underlyingsDatesState.model';

export interface RequestUnderlyingsDatesEpicApi {
  requestUnderlyingsDatesEpicApisBlackoutDates(
    underlyingsDates: UnderlyingsDatesPayload,
  ): Observable<UnderlyingsDatesBlackoutDatesResponse>;

  requestUnderlyingsDatesEpicApisSpecificDates(
    underlyingsDates: UnderlyingsDatesPayload,
  ): Observable<UnderlyingsDatesSpecificDatesResponse>;
}

export function underlyingsBlackoutDatesRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('UNDERLYINGS_BLACKOUT_DATES_REQUESTED'),
      mergeMap(({ underlyingsDatesPayload }) => {
        return underlyingsDatesApiConnector(httpClient)
          .requestUnderlyingsDatesEpicApisBlackoutDates(underlyingsDatesPayload)
          .pipe(
            filter(d => d?.underlyingBloombergCode !== undefined),
            map(dates => {
              return underlyingsBlackoutDatesReceived(buildUnderlyingsDatesKey(underlyingsDatesPayload), {
                underlyingBloombergCode: dates.underlyingBloombergCode,
                blackoutDates: dates.blackoutDates,
              });
            }),
            timeout({ each: timeoutInMillis, scheduler: appScheduler }),
            catchError((err: Error) =>
              of(
                underlyingsBlackoutDatesRequestFailed(
                  underlyingsDatesPayload.productType,
                  underlyingsDatesPayload.productSubType,
                  underlyingsDatesPayload.underlyingBloombergCode,
                  err.message,
                ),
              ),
            ),
          );
      }),
    );
}

export function underlyingsSpecificDatesRequestedEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      ofType('UNDERLYINGS_SPECIFIC_DATES_REQUESTED'),
      mergeMap(({ underlyingsDatesPayload }) => {
        return underlyingsDatesApiConnector(httpClient)
          .requestUnderlyingsDatesEpicApisSpecificDates(underlyingsDatesPayload)
          .pipe(
            filter(d => d?.underlyingBloombergCode !== undefined),
            map(dates => {
              const { underlyingBloombergCode, productType, productSubType, instrumentName, instrumentContractName } =
                underlyingsDatesPayload;
              return underlyingsSpecificDatesReceived(
                {
                  underlyingBloombergCode,
                  productType: instrumentName ?? productType,
                  productSubType: instrumentContractName ?? productSubType,
                },
                { underlyingBloombergCode: dates.underlyingBloombergCode, specificDates: dates.specificDates },
              );
            }),
            timeout({ each: timeoutInMillis, scheduler: appScheduler }),
            catchError((err: Error) =>
              of(
                underlyingsSpecificDatesRequestFailed(
                  underlyingsDatesPayload.productType,
                  underlyingsDatesPayload.productSubType,
                  underlyingsDatesPayload.underlyingBloombergCode,
                  err.message,
                ),
              ),
            ),
          );
      }),
    );
}
